import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import { 
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, 
  verticalListSortingStrategy 
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Save, ArrowLeft, Eye, EyeOff, Smartphone, Monitor, Tablet, 
  RefreshCw, Maximize2, Minimize2, Settings, Layers, Type, Image as ImageIcon,
  GripVertical, Plus, Trash2, Edit, Copy, X, ChevronDown, ChevronRight,
  MousePointer, Layout, FileText, ExternalLink, History, Clock, Upload, FileCheck, Calendar,
  Code, Package, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import ImageUpload from '@/components/admin/ImageUpload';
import { adminApi } from '@/lib/api';

// Sortable Item Component for drag-and-drop
const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  };
  
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div 
        {...attributes} 
        {...listeners} 
        className="absolute left-0 top-1/2 -translate-y-1/2 -ml-6 cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      {children}
    </div>
  );
};

const FullPageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [viewport, setViewport] = useState('desktop');
  const [showSidebar, setShowSidebar] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [inlineEditMode, setInlineEditMode] = useState(false);
  
  // All content data
  const [heroSlides, setHeroSlides] = useState([]);
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [blogPosts, setBlogPosts] = useState([]);
  const [coachingServices, setCoachingServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [statsBlocks, setStatsBlocks] = useState([]);
  
  // Edit state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editType, setEditType] = useState(null);
  const [editItem, setEditItem] = useState(null);
  
  // Template state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [allPages, setAllPages] = useState([]);

  // SEO
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  
  // Draft/Publish Workflow
  const [draft, setDraft] = useState(null);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  
  // Version History
  const [versions, setVersions] = useState([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  
  // Scheduled Publishing
  const [scheduledPublishes, setScheduledPublishes] = useState([]);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  
  // Component Library
  const [componentLibraryOpen, setComponentLibraryOpen] = useState(false);
  const [componentTemplates, setComponentTemplates] = useState([]);
  const [pageSections, setPageSections] = useState([]);
  
  // Custom Code
  const [customCodeOpen, setCustomCodeOpen] = useState(false);
  const [customCode, setCustomCode] = useState({
    custom_css: '',
    custom_js: '',
    head_scripts: '',
    body_scripts: ''
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [pageRes, slidesRes, servicesRes, testimonialsRes, pricingRes, 
             settingsRes, categoriesRes, resourcesRes, pagesRes, coachingRes,
             faqsRes, teamRes, statsRes, draftRes, versionsRes, scheduledRes,
             templatesRes, sectionsRes, customCodeRes] = await Promise.all([
        adminApi.getPage(id),
        adminApi.getHeroSlides(),
        adminApi.getServices(),
        adminApi.getTestimonials(),
        adminApi.getPricing(),
        adminApi.getSiteSettings(),
        adminApi.getCategories(),
        adminApi.getFreeResources(),
        adminApi.getPages(),
        adminApi.getCoachingServices(),
        adminApi.getFaqs(),
        adminApi.getTeamMembers(),
        adminApi.getStatsBlocks(),
        adminApi.getPageDraft(id).catch(() => ({ data: null })),
        adminApi.getPageVersions(id).catch(() => ({ data: [] })),
        adminApi.getScheduledPublishes().catch(() => ({ data: [] })),
        adminApi.getComponentTemplates().catch(() => ({ data: [] })),
        adminApi.getPageSections(id).catch(() => ({ data: [] })),
        adminApi.getPageCustomCode(id).catch(() => ({ data: null }))
      ]);
      
      setPage(pageRes.data);
      setMetaTitle(pageRes.data.meta_title || '');
      setMetaDescription(pageRes.data.meta_description || '');
      setHeroSlides(slidesRes.data || []);
      
      const allServices = servicesRes.data || [];
      setServices(allServices.filter(s => s.service_type === 'main'));
      setSubServices(allServices.filter(s => s.service_type === 'sub'));
      
      setTestimonials(testimonialsRes.data || []);
      setPricing(pricingRes.data || []);
      setSiteSettings(settingsRes.data || {});
      setCategories(categoriesRes.data || []);
      setResources(resourcesRes.data || []);
      setAllPages(pagesRes.data || []);
      setCoachingServices(coachingRes.data || []);
      setFaqs(faqsRes.data || []);
      setTeamMembers(teamRes.data || []);
      setStatsBlocks(statsRes.data || []);
      
      // Draft/Publish workflow
      setDraft(draftRes.data);
      setIsDraftMode(!!draftRes.data);
      setVersions(versionsRes.data || []);
      setScheduledPublishes((scheduledRes.data || []).filter(s => s.page_id === id));
      
      // Component Library & Custom Code
      setComponentTemplates(templatesRes.data || []);
      setPageSections(sectionsRes.data || []);
      if (customCodeRes.data) {
        setCustomCode({
          custom_css: customCodeRes.data.custom_css || '',
          custom_js: customCodeRes.data.custom_js || '',
          head_scripts: customCodeRes.data.head_scripts || '',
          body_scripts: customCodeRes.data.body_scripts || ''
        });
      }
      
      
    } catch (error) {
      toast.error('Failed to load page data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updatePage(id, {
        ...page,
        meta_title: metaTitle,
        meta_description: metaDescription
      });
      toast.success('Page saved successfully');
      setHasChanges(false);
      refreshIframe();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const refreshIframe = () => {
    if (iframeRef.current) {
      setIframeLoaded(false);
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event, items, setItems, updateFn) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      
      // Update order in database
      try {
        for (let i = 0; i < newItems.length; i++) {
          await updateFn(newItems[i].id, { ...newItems[i], order: i });
        }
        toast.success('Order updated');
        setHasChanges(true);
      } catch (error) {
        toast.error('Failed to update order');
      }
    }
  };

  // Open edit modal
  const openEdit = (type, item) => {
    setEditType(type);
    setEditItem({ ...item });
    setEditModalOpen(true);
  };

  // Save edit
  const saveEdit = async () => {
    try {
      switch (editType) {
        case 'hero_slide':
          await adminApi.updateHeroSlide(editItem.id, editItem);
          const updatedSlides = heroSlides.map(s => s.id === editItem.id ? editItem : s);
          setHeroSlides(updatedSlides);
          break;
        case 'service':
          await adminApi.updateService(editItem.id, editItem);
          if (editItem.service_type === 'main') {
            setServices(services.map(s => s.id === editItem.id ? editItem : s));
          } else {
            setSubServices(subServices.map(s => s.id === editItem.id ? editItem : s));
          }
          break;
        case 'testimonial':
          await adminApi.updateTestimonial(editItem.id, editItem);
          setTestimonials(testimonials.map(t => t.id === editItem.id ? editItem : t));
          break;
        case 'pricing':
          await adminApi.updatePricing(editItem.id, editItem);
          setPricing(pricing.map(p => p.id === editItem.id ? editItem : p));
          break;
        case 'resource':
          await adminApi.updateFreeResource(editItem.id, editItem);
          setResources(resources.map(r => r.id === editItem.id ? editItem : r));
          break;
        case 'coaching_service':
          await adminApi.updateCoachingService(editItem.id, editItem);
          setCoachingServices(coachingServices.map(c => c.id === editItem.id ? editItem : c));
          break;
        case 'blog_post':
          await adminApi.updateBlogPost(editItem.id, editItem);
          break;
        case 'faq':
          await adminApi.updateFaq(editItem.id, editItem);
          setFaqs(faqs.map(f => f.id === editItem.id ? editItem : f));
          break;
        case 'team_member':
          await adminApi.updateTeamMember(editItem.id, editItem);
          setTeamMembers(teamMembers.map(t => t.id === editItem.id ? editItem : t));
          break;
        case 'stats_block':
          await adminApi.updateStatsBlock(editItem.id, editItem);
          setStatsBlocks(statsBlocks.map(s => s.id === editItem.id ? editItem : s));
          break;
      }
      toast.success('Changes saved');
      setEditModalOpen(false);
      setHasChanges(true);
      refreshIframe();
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };

  // Delete item
  const deleteItem = async (type, itemId) => {
    try {
      switch (type) {
        case 'hero_slide':
          await adminApi.deleteHeroSlide(itemId);
          setHeroSlides(heroSlides.filter(s => s.id !== itemId));
          break;
        case 'service':
          await adminApi.deleteService(itemId);
          setServices(services.filter(s => s.id !== itemId));
          setSubServices(subServices.filter(s => s.id !== itemId));
          break;
        case 'testimonial':
          await adminApi.deleteTestimonial(itemId);
          setTestimonials(testimonials.filter(t => t.id !== itemId));
          break;
        case 'faq':
          await adminApi.deleteFaq(itemId);
          setFaqs(faqs.filter(f => f.id !== itemId));
          break;
        case 'team_member':
          await adminApi.deleteTeamMember(itemId);
          setTeamMembers(teamMembers.filter(t => t.id !== itemId));
          break;
        case 'stats_block':
          await adminApi.deleteStatsBlock(itemId);
          setStatsBlocks(statsBlocks.filter(s => s.id !== itemId));
          break;
      }
      toast.success('Deleted successfully');
      setHasChanges(true);
      refreshIframe();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Create from template
  const createFromTemplate = async (templatePage) => {
    try {
      const newPage = {
        title: `Copy of ${templatePage.title}`,
        slug: `${templatePage.slug}-copy-${Date.now()}`,
        path: `${templatePage.path}-copy`,
        content: templatePage.content,
        meta_title: templatePage.meta_title,
        meta_description: templatePage.meta_description,
        is_active: false
      };
      await adminApi.createPage(newPage);
      toast.success('Page created from template');
      setTemplateModalOpen(false);
      navigate('/admin/pages');
    } catch (error) {
      toast.error('Failed to create page');
    }
  };

  // Get sections based on page
  const getPageSections = () => {
    const slug = page?.slug || '';
    const path = page?.path || '';
    
    if (slug === 'home' || path === '/') {
      return [
        { id: 'hero', name: 'Hero Slides', icon: ImageIcon, items: heroSlides, type: 'hero_slide', sortable: true },
        { id: 'services', name: 'Main Services', icon: Layers, items: services, type: 'service', sortable: true },
        { id: 'subservices', name: 'Sub Services', icon: Layers, items: subServices, type: 'service', sortable: true },
        { id: 'testimonials', name: 'Testimonials', icon: Type, items: testimonials, type: 'testimonial', sortable: true },
        { id: 'pricing', name: 'Pricing Plans', icon: Type, items: pricing, type: 'pricing', sortable: false },
        { id: 'stats', name: 'Stats Blocks', icon: Type, items: statsBlocks, type: 'stats_block', sortable: true }
      ];
    } else if (slug === 'about') {
      return [
        { id: 'about_hero', name: 'About Hero', icon: Type, items: [siteSettings], type: 'settings', sortable: false },
        { id: 'team', name: 'Team Members', icon: Type, items: teamMembers, type: 'team_member', sortable: true },
        { id: 'testimonials', name: 'Testimonials', icon: Type, items: testimonials, type: 'testimonial', sortable: true },
        { id: 'stats', name: 'Stats Blocks', icon: Type, items: statsBlocks, type: 'stats_block', sortable: true }
      ];
    } else if (slug === 'services') {
      return [
        { id: 'services', name: 'Main Services', icon: Layers, items: services, type: 'service', sortable: true },
        { id: 'coaching', name: 'Coaching Services', icon: Layers, items: coachingServices, type: 'coaching_service', sortable: true },
        { id: 'subservices', name: 'Sub Services', icon: Layers, items: subServices, type: 'service', sortable: true },
        { id: 'faq', name: 'FAQ', icon: Type, items: faqs.filter(f => f.category === 'services'), type: 'faq', sortable: true }
      ];
    } else if (slug === 'free-resources') {
      return [
        { id: 'resources', name: 'Free Resources', icon: FileText, items: resources, type: 'resource', sortable: true },
        { id: 'categories', name: 'Categories', icon: Layers, items: categories, type: 'category', sortable: false }
      ];
    } else if (slug === 'blog') {
      return [
        { id: 'categories', name: 'Blog Categories', icon: Layers, items: categories, type: 'category', sortable: false }
      ];
    } else if (slug === 'contact-us') {
      return [
        { id: 'contact', name: 'Contact Info', icon: Settings, items: [siteSettings], type: 'settings', sortable: false },
        { id: 'faq', name: 'FAQ', icon: Type, items: faqs.filter(f => f.category === 'general'), type: 'faq', sortable: true }
      ];
    } else if (slug === 'workshops') {
      return [
        { id: 'services', name: 'Workshop Services', icon: Layers, items: services.filter(s => s.title?.includes('Workshop')), type: 'service', sortable: true }
      ];
    } else if (slug?.includes('career-strategy') || path?.includes('career-strategy')) {
      return [
        { id: 'pricing', name: 'Pricing', icon: Type, items: pricing.filter(p => p.name?.includes('Strategy')), type: 'pricing', sortable: false },
        { id: 'testimonials', name: 'Success Stories', icon: Type, items: testimonials, type: 'testimonial', sortable: true },
        { id: 'faq', name: 'FAQ', icon: Type, items: faqs.filter(f => f.category === 'services'), type: 'faq', sortable: true }
      ];
    } else if (slug?.includes('career-coaching') || path?.includes('career-coaching')) {
      return [
        { id: 'pricing', name: 'Coaching Packages', icon: Type, items: pricing.filter(p => p.name?.includes('Month') || p.name?.includes('Package')), type: 'pricing', sortable: false },
        { id: 'testimonials', name: 'Success Stories', icon: Type, items: testimonials, type: 'testimonial', sortable: true },
        { id: 'faq', name: 'FAQ', icon: Type, items: faqs.filter(f => f.category === 'pricing' || f.category === 'services'), type: 'faq', sortable: true }
      ];
    }
    
    return [
      { id: 'content', name: 'Page Content', icon: Type, items: [], type: 'content', sortable: false },
      { id: 'faq', name: 'FAQ', icon: Type, items: faqs, type: 'faq', sortable: true }
    ];
  };
  
  // Draft/Publish Workflow Functions
  const saveDraft = async () => {
    setSaving(true);
    try {
      await adminApi.savePageDraft(id, {
        content: page?.content || {},
        blocks: []
      });
      setIsDraftMode(true);
      setHasChanges(false);
      toast.success('Draft saved');
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };
  
  const publishDraft = async () => {
    setSaving(true);
    try {
      await adminApi.publishPageDraft(id);
      setIsDraftMode(false);
      setDraft(null);
      toast.success('Draft published successfully');
      refreshIframe();
      // Refresh versions
      const versionsRes = await adminApi.getPageVersions(id);
      setVersions(versionsRes.data || []);
    } catch (error) {
      toast.error('Failed to publish draft');
    } finally {
      setSaving(false);
      setPublishModalOpen(false);
    }
  };
  
  const discardDraft = async () => {
    try {
      await adminApi.discardPageDraft(id);
      setIsDraftMode(false);
      setDraft(null);
      toast.success('Draft discarded');
      refreshIframe();
    } catch (error) {
      toast.error('Failed to discard draft');
    }
  };
  
  // Version History Functions
  const rollbackToVersion = async (versionId) => {
    setSaving(true);
    try {
      await adminApi.rollbackToVersion(versionId);
      toast.success('Rolled back successfully');
      setHistoryModalOpen(false);
      fetchAllData(); // Refresh all data
    } catch (error) {
      toast.error('Failed to rollback');
    } finally {
      setSaving(false);
    }
  };
  
  // Scheduled Publishing Functions
  const schedulePublish = async () => {
    if (!scheduleDate) {
      toast.error('Please select a date and time');
      return;
    }
    
    setSaving(true);
    try {
      await adminApi.createScheduledPublish(id, scheduleDate);
      toast.success('Publishing scheduled');
      setScheduleModalOpen(false);
      setScheduleDate('');
      // Refresh scheduled publishes
      const scheduledRes = await adminApi.getScheduledPublishes();
      setScheduledPublishes((scheduledRes.data || []).filter(s => s.page_id === id));
    } catch (error) {
      toast.error('Failed to schedule publish');
    } finally {
      setSaving(false);
    }
  };
  
  const cancelScheduledPublish = async (scheduleId) => {
    try {
      await adminApi.cancelScheduledPublish(scheduleId);
      setScheduledPublishes(scheduledPublishes.filter(s => s.id !== scheduleId));
      toast.success('Scheduled publish cancelled');
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };
  
  // Component Library Functions
  const addSectionFromTemplate = async (template) => {
    try {
      const newSection = {
        page_id: id,
        component_id: template.id,
        section_name: template.name,
        content: template.default_content || {},
        order: pageSections.length,
        is_visible: true,
        custom_css: '',
        custom_js: ''
      };
      await adminApi.createPageSection(newSection);
      toast.success('Section added');
      setComponentLibraryOpen(false);
      // Refresh sections
      const sectionsRes = await adminApi.getPageSections(id);
      setPageSections(sectionsRes.data || []);
      refreshIframe();
    } catch (error) {
      toast.error('Failed to add section');
    }
  };
  
  const deletePageSection = async (sectionId) => {
    try {
      await adminApi.deletePageSection(sectionId);
      setPageSections(pageSections.filter(s => s.id !== sectionId));
      toast.success('Section removed');
      refreshIframe();
    } catch (error) {
      toast.error('Failed to remove section');
    }
  };
  
  // Section drag-and-drop reordering
  const handleSectionDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = pageSections.findIndex(s => s.id === active.id);
      const newIndex = pageSections.findIndex(s => s.id === over.id);
      const newSections = arrayMove(pageSections, oldIndex, newIndex);
      setPageSections(newSections);
      
      try {
        const sectionIds = newSections.map(s => s.id);
        await adminApi.reorderPageSections(id, sectionIds);
        toast.success('Section order updated');
        refreshIframe();
      } catch (error) {
        toast.error('Failed to reorder sections');
      }
    }
  };
  
  // Custom Code Functions
  const saveCustomCode = async () => {
    setSaving(true);
    try {
      await adminApi.updatePageCustomCode(id, {
        page_id: id,
        ...customCode,
        is_active: true
      });
      toast.success('Custom code saved');
      setCustomCodeOpen(false);
      refreshIframe();
    } catch (error) {
      toast.error('Failed to save custom code');
    } finally {
      setSaving(false);
    }
  };

  const viewportSizes = {
    desktop: { width: '100%', maxWidth: '100%' },
    tablet: { width: '768px', maxWidth: '768px' },
    mobile: { width: '375px', maxWidth: '375px' }
  };

  const pageUrl = page?.path || '/';
  const previewUrl = `${window.location.origin}${pageUrl}`;

  // Send edit mode status to iframe
  const sendEditModeToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: inlineEditMode ? 'ENABLE_EDIT_MODE' : 'DISABLE_EDIT_MODE' },
        '*'
      );
    }
  }, [inlineEditMode]);

  // Send edit mode when toggle changes
  useEffect(() => {
    sendEditModeToIframe();
  }, [inlineEditMode, sendEditModeToIframe]);

  // Inline edit handler for iframe
  const handleIframeMessage = useCallback((event) => {
    // Handle iframe ready message
    if (event.data?.type === 'IFRAME_READY') {
      sendEditModeToIframe();
    }
    
    // Handle element clicked message from iframe
    if (event.data?.type === 'ELEMENT_CLICKED') {
      const { elementType, elementId, elementData } = event.data;
      
      // Find the item in our data and open the edit modal
      let item = null;
      
      switch (elementType) {
        case 'hero_slide':
          item = heroSlides.find(s => s.id === elementId);
          break;
        case 'service':
          item = services.find(s => s.id === elementId) || subServices.find(s => s.id === elementId);
          break;
        case 'testimonial':
          item = testimonials.find(t => t.id === elementId);
          break;
        case 'pricing':
          item = pricing.find(p => p.id === elementId);
          break;
        case 'coaching_service':
          item = coachingServices.find(c => c.id === elementId);
          break;
        case 'resource':
          item = resources.find(r => r.id === elementId);
          break;
        case 'faq':
          item = faqs.find(f => f.id === elementId);
          break;
        case 'team_member':
          item = teamMembers.find(t => t.id === elementId);
          break;
        case 'stats_block':
          item = statsBlocks.find(s => s.id === elementId);
          break;
        default:
          item = elementData;
      }
      
      if (item) {
        openEdit(elementType, item);
      }
    }
  }, [heroSlides, services, subServices, testimonials, pricing, coachingServices, resources, sendEditModeToIframe]);

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading page editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="full-page-editor">
      {/* Top Toolbar */}
      <div className="h-14 border-b bg-white flex items-center justify-between px-4 shrink-0 z-50">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/pages')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <Badge variant="outline" className="font-medium">{page?.title}</Badge>
          <Badge variant="secondary">{page?.path}</Badge>
          {hasChanges && <Badge className="bg-orange-500">Unsaved</Badge>}
          {isDraftMode && <Badge className="bg-yellow-500">Draft</Badge>}
          {scheduledPublishes.length > 0 && <Badge className="bg-purple-500">Scheduled</Badge>}
        </div>

        <div className="flex items-center gap-2">
          {/* Inline Edit Toggle */}
          <div className="flex items-center gap-2 mr-2">
            <Switch 
              checked={inlineEditMode} 
              onCheckedChange={setInlineEditMode}
              id="inline-edit"
            />
            <Label htmlFor="inline-edit" className="text-sm cursor-pointer">
              <MousePointer className="w-4 h-4 inline mr-1" />
              Inline Edit
            </Label>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Viewport Toggle */}
          <div className="flex items-center border rounded-lg p-1 bg-muted/50">
            {[
              { id: 'desktop', icon: Monitor },
              { id: 'tablet', icon: Tablet },
              { id: 'mobile', icon: Smartphone }
            ].map(v => (
              <Button
                key={v.id}
                variant={viewport === v.id ? 'secondary' : 'ghost'}
                size="sm"
                className="px-2"
                onClick={() => setViewport(v.id)}
              >
                <v.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={refreshIframe}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')}>
            <ExternalLink className="w-4 h-4" />
          </Button>

          <Button variant="outline" size="sm" onClick={() => setShowSidebar(!showSidebar)}>
            {showSidebar ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>

          {/* Version History */}
          <Button variant="outline" size="sm" onClick={() => setHistoryModalOpen(true)}>
            <History className="w-4 h-4 mr-1" /> History
          </Button>

          {/* Schedule Publish */}
          <Button variant="outline" size="sm" onClick={() => setScheduleModalOpen(true)}>
            <Calendar className="w-4 h-4 mr-1" /> Schedule
          </Button>

          {/* Component Library */}
          <Button variant="outline" size="sm" onClick={() => setComponentLibraryOpen(true)}>
            <Package className="w-4 h-4 mr-1" /> Add Section
          </Button>

          {/* Custom Code */}
          <Button variant="outline" size="sm" onClick={() => setCustomCodeOpen(true)}>
            <Code className="w-4 h-4 mr-1" /> Code
          </Button>

          <Button variant="outline" size="sm" onClick={() => setTemplateModalOpen(true)}>
            <Copy className="w-4 h-4 mr-2" /> Template
          </Button>

          {/* Draft/Publish Workflow */}
          {isDraftMode ? (
            <>
              <Button variant="outline" size="sm" onClick={discardDraft}>
                <X className="w-4 h-4 mr-1" /> Discard Draft
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setPublishModalOpen(true)}>
                <Upload className="w-4 h-4 mr-1" /> Publish
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving}>
                <FileCheck className="w-4 h-4 mr-1" /> Save Draft
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Content - Live Preview */}
        <div className="flex-1 bg-muted/30 p-4 overflow-auto flex justify-center">
          <div 
            className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 relative"
            style={viewportSizes[viewport]}
          >
            {inlineEditMode && (
              <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-xs py-1 px-2 z-10">
                Inline Edit Mode: Click on any text or image in the preview to edit
              </div>
            )}
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={previewUrl}
              className="w-full h-full border-0"
              style={{ minHeight: 'calc(100vh - 120px)', marginTop: inlineEditMode ? '24px' : 0 }}
              onLoad={() => {
                setIframeLoaded(true);
                // Send edit mode status after iframe loads
                setTimeout(sendEditModeToIframe, 100);
              }}
              title="Page Preview"
            />
          </div>
        </div>

        {/* Right Sidebar */}
        {showSidebar && (
          <div className="w-[400px] border-l bg-white overflow-hidden flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <div className="border-b px-4 py-2">
                <TabsList className="w-full">
                  <TabsTrigger value="content" className="flex-1">Content</TabsTrigger>
                  <TabsTrigger value="layout" className="flex-1">Layout</TabsTrigger>
                  <TabsTrigger value="seo" className="flex-1">SEO</TabsTrigger>
                  <TabsTrigger value="settings" className="flex-1">Settings</TabsTrigger>
                </TabsList>
              </div>
              
              <ScrollArea className="flex-1">
                <TabsContent value="content" className="p-4 m-0">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Drag items to reorder. Click Edit to modify content.
                    </p>
                    
                    {getPageSections().map((section) => (
                      <Collapsible key={section.id} defaultOpen>
                        <Card>
                          <CollapsibleTrigger asChild>
                            <CardHeader className="cursor-pointer hover:bg-muted/50 py-3">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <section.icon className="w-4 h-4" />
                                {section.name}
                                <Badge variant="secondary" className="ml-auto">
                                  {section.items?.length || 0}
                                </Badge>
                                <ChevronDown className="w-4 h-4" />
                              </CardTitle>
                            </CardHeader>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent className="pt-0 space-y-2">
                              {section.sortable && section.items?.length > 0 ? (
                                <DndContext
                                  sensors={sensors}
                                  collisionDetection={closestCenter}
                                  onDragEnd={(e) => handleDragEnd(
                                    e, 
                                    section.items, 
                                    section.type === 'hero_slide' ? setHeroSlides :
                                    section.type === 'service' ? (section.id === 'services' ? setServices : setSubServices) :
                                    section.type === 'testimonial' ? setTestimonials :
                                    section.type === 'coaching_service' ? setCoachingServices :
                                    setResources,
                                    section.type === 'hero_slide' ? adminApi.updateHeroSlide :
                                    section.type === 'service' ? adminApi.updateService :
                                    section.type === 'testimonial' ? adminApi.updateTestimonial :
                                    section.type === 'coaching_service' ? adminApi.updateCoachingService :
                                    adminApi.updateFreeResource
                                  )}
                                >
                                  <SortableContext items={section.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                                    {section.items.map((item) => (
                                      <SortableItem key={item.id} id={item.id}>
                                        <div className="p-3 border rounded-lg hover:bg-muted/50 transition-colors ml-4">
                                          <div className="flex items-center gap-3">
                                            {(item.image_url || item.image) && (
                                              <img src={item.image_url || item.image} alt="" className="w-12 h-12 object-cover rounded" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <p className="font-medium text-sm truncate">{item.title || item.name}</p>
                                              <p className="text-xs text-muted-foreground truncate">
                                                {item.description?.slice(0, 40) || item.content?.slice(0, 40) || item.role || ''}...
                                              </p>
                                            </div>
                                            <div className="flex gap-1">
                                              <Button 
                                                variant="ghost" 
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => openEdit(section.type, item)}
                                              >
                                                <Edit className="w-4 h-4" />
                                              </Button>
                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                                    <Trash2 className="w-4 h-4" />
                                                  </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                  <AlertDialogHeader>
                                                    <AlertDialogTitle>Delete Item</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                      Are you sure you want to delete "{item.title || item.name}"?
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => deleteItem(section.type, item.id)}>Delete</AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          </div>
                                        </div>
                                      </SortableItem>
                                    ))}
                                  </SortableContext>
                                </DndContext>
                              ) : (
                                section.items?.map((item) => (
                                  <div 
                                    key={item.id || 'settings'} 
                                    className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => {
                                      if (section.type === 'settings') {
                                        navigate('/admin/settings');
                                      } else if (section.type === 'pricing') {
                                        navigate('/admin/pricing');
                                      } else if (section.type === 'category') {
                                        navigate('/admin/categories');
                                      } else {
                                        openEdit(section.type, item);
                                      }
                                    }}
                                  >
                                    <div className="flex items-center justify-between">
                                      <p className="font-medium text-sm">{item.title || item.name || 'Site Settings'}</p>
                                      {item.price && <Badge>{item.price}</Badge>}
                                      <Button variant="ghost" size="sm">Edit</Button>
                                    </div>
                                  </div>
                                ))
                              )}
                              
                              {section.items?.length === 0 && (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                  No items in this section
                                </p>
                              )}
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    ))}
                    
                    {/* Quick Links */}
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Quick Edit</CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-2">
                        <Button variant="outline" size="sm" className="justify-start" onClick={() => navigate('/admin/slides')}>
                          <ImageIcon className="w-4 h-4 mr-2" /> Slides
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start" onClick={() => navigate('/admin/services')}>
                          <Layers className="w-4 h-4 mr-2" /> Services
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start" onClick={() => navigate('/admin/testimonials')}>
                          <Type className="w-4 h-4 mr-2" /> Reviews
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start" onClick={() => navigate('/admin/pricing')}>
                          <Type className="w-4 h-4 mr-2" /> Pricing
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start" onClick={() => navigate('/admin/coaching-services')}>
                          <Layout className="w-4 h-4 mr-2" /> Coaching
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start" onClick={() => navigate('/admin/resources')}>
                          <FileText className="w-4 h-4 mr-2" /> Resources
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                <TabsContent value="layout" className="p-4 m-0">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Page Sections</h3>
                        <p className="text-sm text-muted-foreground">Drag to reorder sections on the page</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setComponentLibraryOpen(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    
                    {pageSections.length === 0 ? (
                      <Card className="py-8">
                        <CardContent className="text-center">
                          <Layout className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                          <p className="text-sm text-muted-foreground mb-3">No custom sections yet</p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setComponentLibraryOpen(true)}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add Section from Library
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleSectionDragEnd}
                      >
                        <SortableContext items={pageSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                          <div className="space-y-2">
                            {pageSections.map((section, index) => (
                              <SortableItem key={section.id} id={section.id}>
                                <Card className="ml-4">
                                  <CardContent className="py-3 px-4">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-6 h-6 rounded bg-muted text-xs font-medium">
                                          {index + 1}
                                        </div>
                                        <div>
                                          <p className="font-medium text-sm">{section.section_name}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {section.is_visible ? 'Visible' : 'Hidden'}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={() => {
                                            // Toggle visibility
                                            const updated = { ...section, is_visible: !section.is_visible };
                                            adminApi.updatePageSection(section.id, updated);
                                            setPageSections(pageSections.map(s => s.id === section.id ? updated : s));
                                            refreshIframe();
                                          }}
                                        >
                                          {section.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          className="text-destructive hover:text-destructive"
                                          onClick={() => deletePageSection(section.id)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </SortableItem>
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                    
                    <Separator />
                    
                    <div>
                      <h4 className="font-medium text-sm mb-2">Default Page Sections</h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        These are the standard sections for this page type. Use the Content tab to edit them.
                      </p>
                      <div className="space-y-2">
                        {getPageSections().map((section) => (
                          <div key={section.id} className="flex items-center gap-3 p-2 border rounded-lg bg-muted/30">
                            <section.icon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{section.name}</span>
                            <Badge variant="outline" className="ml-auto text-xs">
                              {section.items?.length || 0} items
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="seo" className="p-4 m-0">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Meta Title</Label>
                      <Input
                        value={metaTitle}
                        onChange={(e) => {
                          setMetaTitle(e.target.value);
                          setHasChanges(true);
                        }}
                        placeholder="Page title for search engines..."
                      />
                      <p className="text-xs text-muted-foreground">
                        {metaTitle.length}/60 characters
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Meta Description</Label>
                      <Textarea
                        value={metaDescription}
                        onChange={(e) => {
                          setMetaDescription(e.target.value);
                          setHasChanges(true);
                        }}
                        placeholder="Page description for search engines..."
                        rows={4}
                      />
                      <p className="text-xs text-muted-foreground">
                        {metaDescription.length}/160 characters
                      </p>
                    </div>
                    
                    <Separator />
                    
                    <Card className="bg-muted/50">
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Search Preview</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm">
                        <p className="text-blue-600 font-medium truncate">
                          {metaTitle || page?.title || 'Page Title'}
                        </p>
                        <p className="text-green-700 text-xs truncate">
                          {window.location.origin}{page?.path}
                        </p>
                        <p className="text-gray-600 text-xs line-clamp-2">
                          {metaDescription || 'Add a meta description to improve SEO...'}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                <TabsContent value="settings" className="p-4 m-0">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Page Title</Label>
                      <Input
                        value={page?.title || ''}
                        onChange={(e) => {
                          setPage({ ...page, title: e.target.value });
                          setHasChanges(true);
                        }}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>URL Path</Label>
                      <Input value={page?.path || ''} disabled className="bg-muted" />
                      <p className="text-xs text-muted-foreground">
                        URL paths cannot be changed after creation
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label>Page Status</Label>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={page?.is_active}
                          onCheckedChange={(checked) => {
                            setPage({ ...page, is_active: checked });
                            setHasChanges(true);
                          }}
                        />
                        <span className="text-sm">{page?.is_active ? 'Active' : 'Draft'}</span>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Slug</span>
                        <code className="bg-muted px-2 py-0.5 rounded text-xs">{page?.slug}</code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created</span>
                        <span>{new Date(page?.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Updated</span>
                        <span>{new Date(page?.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit {editType === 'hero_slide' ? 'Hero Slide' : 
                    editType === 'service' ? 'Service' : 
                    editType === 'testimonial' ? 'Testimonial' :
                    editType === 'coaching_service' ? 'Coaching Service' :
                    editType === 'pricing' ? 'Pricing Plan' :
                    editType === 'resource' ? 'Resource' :
                    editType === 'blog_post' ? 'Blog Post' :
                    editType === 'faq' ? 'FAQ' :
                    editType === 'team_member' ? 'Team Member' :
                    editType === 'stats_block' ? 'Stats Block' : 'Item'}
            </DialogTitle>
            <DialogDescription>Make changes and click Save</DialogDescription>
          </DialogHeader>
          
          {editItem && (
            <div className="space-y-4 py-4">
              {/* Hero Slide Fields */}
              {editType === 'hero_slide' && (
                <>
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={editItem.title || ''}
                      onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={editItem.description || ''}
                      onChange={(e) => setEditItem({...editItem, description: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <ImageUpload
                    label="Slide Image"
                    value={editItem.image_url || ''}
                    onChange={(url) => setEditItem({...editItem, image_url: url})}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Button Text</Label>
                      <Input
                        value={editItem.button_text || ''}
                        onChange={(e) => setEditItem({...editItem, button_text: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Button Link</Label>
                      <Input
                        value={editItem.button_link || ''}
                        onChange={(e) => setEditItem({...editItem, button_link: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Secondary Button</Label>
                      <Input
                        value={editItem.secondary_button_text || ''}
                        onChange={(e) => setEditItem({...editItem, secondary_button_text: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Link</Label>
                      <Input
                        value={editItem.secondary_button_link || ''}
                        onChange={(e) => setEditItem({...editItem, secondary_button_link: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="space-y-2">
                      <Label>Order</Label>
                      <Input
                        type="number"
                        value={editItem.order || 0}
                        onChange={(e) => setEditItem({...editItem, order: parseInt(e.target.value)})}
                        className="w-20"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={editItem.is_active}
                        onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                </>
              )}
              
              {/* Service Fields */}
              {editType === 'service' && (
                <>
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={editItem.title || ''}
                      onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={editItem.description || ''}
                      onChange={(e) => setEditItem({...editItem, description: e.target.value})}
                      rows={4}
                    />
                  </div>
                  <ImageUpload
                    label="Service Image"
                    value={editItem.image_url || ''}
                    onChange={(url) => setEditItem({...editItem, image_url: url})}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Link URL</Label>
                      <Input
                        value={editItem.link || ''}
                        onChange={(e) => setEditItem({...editItem, link: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Icon</Label>
                      <Input
                        value={editItem.icon || ''}
                        onChange={(e) => setEditItem({...editItem, icon: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editItem.is_active !== false}
                      onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                    />
                    <Label>Active</Label>
                  </div>
                </>
              )}
              
              {/* Testimonial Fields */}
              {editType === 'testimonial' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={editItem.name || ''}
                        onChange={(e) => setEditItem({...editItem, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role / Title</Label>
                      <Input
                        value={editItem.role || ''}
                        onChange={(e) => setEditItem({...editItem, role: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Testimonial Content *</Label>
                    <Textarea
                      value={editItem.content || ''}
                      onChange={(e) => setEditItem({...editItem, content: e.target.value})}
                      rows={4}
                    />
                  </div>
                  <ImageUpload
                    label="Client Photo"
                    value={editItem.image_url || ''}
                    onChange={(url) => setEditItem({...editItem, image_url: url})}
                  />
                  <div className="space-y-2">
                    <Label>Rating</Label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(r => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setEditItem({...editItem, rating: r})}
                          className={`text-2xl ${r <= (editItem.rating || 5) ? 'text-yellow-500' : 'text-gray-300'}`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              {/* Coaching Service Fields */}
              {editType === 'coaching_service' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={editItem.title || ''}
                        onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tagline</Label>
                      <Input
                        value={editItem.tagline || ''}
                        onChange={(e) => setEditItem({...editItem, tagline: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Short Description</Label>
                    <Textarea
                      value={editItem.short_description || ''}
                      onChange={(e) => setEditItem({...editItem, short_description: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <ImageUpload
                    label="Service Image"
                    value={editItem.image || ''}
                    onChange={(url) => setEditItem({...editItem, image: url})}
                  />
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        value={editItem.pricing_one_time || 250}
                        onChange={(e) => setEditItem({...editItem, pricing_one_time: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stat 1 Value</Label>
                      <Input
                        value={editItem.stat_1_value || ''}
                        onChange={(e) => setEditItem({...editItem, stat_1_value: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stat 2 Value</Label>
                      <Input
                        value={editItem.stat_2_value || ''}
                        onChange={(e) => setEditItem({...editItem, stat_2_value: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editItem.is_popular}
                        onCheckedChange={(c) => setEditItem({...editItem, is_popular: c})}
                      />
                      <Label>Most Popular</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editItem.is_active !== false}
                        onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                </>
              )}
              
              {/* Resource Fields */}
              {editType === 'resource' && (
                <>
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={editItem.title || ''}
                      onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={editItem.description || ''}
                      onChange={(e) => setEditItem({...editItem, description: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Link URL</Label>
                      <Input
                        value={editItem.link || ''}
                        onChange={(e) => setEditItem({...editItem, link: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Input
                        value={editItem.category || ''}
                        onChange={(e) => setEditItem({...editItem, category: e.target.value})}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Blog Post Fields */}
              {editType === 'blog_post' && (
                <>
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={editItem.title || ''}
                      onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Excerpt</Label>
                    <Textarea
                      value={editItem.excerpt || ''}
                      onChange={(e) => setEditItem({...editItem, excerpt: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Featured Image</Label>
                    <ImageUpload
                      value={editItem.featured_image}
                      onChange={(url) => setEditItem({...editItem, featured_image: url})}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editItem.is_published !== false}
                      onCheckedChange={(c) => setEditItem({...editItem, is_published: c})}
                    />
                    <Label>Published</Label>
                  </div>
                </>
              )}

              {/* Pricing Fields */}
              {editType === 'pricing' && (
                <>
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={editItem.title || ''}
                      onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <Input
                        value={editItem.price || ''}
                        onChange={(e) => setEditItem({...editItem, price: e.target.value})}
                        placeholder="e.g., $150, FREE"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Duration</Label>
                      <Input
                        value={editItem.duration || ''}
                        onChange={(e) => setEditItem({...editItem, duration: e.target.value})}
                        placeholder="e.g., session, month"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={editItem.description || ''}
                      onChange={(e) => setEditItem({...editItem, description: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Features (one per line)</Label>
                    <Textarea
                      value={(editItem.features || []).join('\n')}
                      onChange={(e) => setEditItem({...editItem, features: e.target.value.split('\n').filter(f => f.trim())})}
                      rows={4}
                      placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Button Text</Label>
                      <Input
                        value={editItem.button_text || ''}
                        onChange={(e) => setEditItem({...editItem, button_text: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Button Link</Label>
                      <Input
                        value={editItem.button_link || ''}
                        onChange={(e) => setEditItem({...editItem, button_link: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editItem.is_featured}
                        onCheckedChange={(c) => setEditItem({...editItem, is_featured: c})}
                      />
                      <Label>Featured (Most Popular)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editItem.is_active !== false}
                        onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                </>
              )}

              {/* FAQ Fields */}
              {editType === 'faq' && (
                <>
                  <div className="space-y-2">
                    <Label>Question *</Label>
                    <Input
                      value={editItem.question || ''}
                      onChange={(e) => setEditItem({...editItem, question: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Answer *</Label>
                    <Textarea
                      value={editItem.answer || ''}
                      onChange={(e) => setEditItem({...editItem, answer: e.target.value})}
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Input
                        value={editItem.category || ''}
                        onChange={(e) => setEditItem({...editItem, category: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Order</Label>
                      <Input
                        type="number"
                        value={editItem.order || 0}
                        onChange={(e) => setEditItem({...editItem, order: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editItem.is_active !== false}
                      onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                    />
                    <Label>Active</Label>
                  </div>
                </>
              )}

              {/* Team Member Fields */}
              {editType === 'team_member' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={editItem.name || ''}
                        onChange={(e) => setEditItem({...editItem, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <Input
                        value={editItem.role || ''}
                        onChange={(e) => setEditItem({...editItem, role: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea
                      value={editItem.bio || ''}
                      onChange={(e) => setEditItem({...editItem, bio: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Profile Image</Label>
                    <ImageUpload
                      value={editItem.image_url}
                      onChange={(url) => setEditItem({...editItem, image_url: url})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        value={editItem.email || ''}
                        onChange={(e) => setEditItem({...editItem, email: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>LinkedIn URL</Label>
                      <Input
                        value={editItem.linkedin_url || ''}
                        onChange={(e) => setEditItem({...editItem, linkedin_url: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editItem.is_active !== false}
                      onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                    />
                    <Label>Active</Label>
                  </div>
                </>
              )}

              {/* Stats Block Fields */}
              {editType === 'stats_block' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={editItem.title || ''}
                        onChange={(e) => setEditItem({...editItem, title: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Value *</Label>
                      <Input
                        value={editItem.value || ''}
                        onChange={(e) => setEditItem({...editItem, value: e.target.value})}
                        placeholder="e.g., 500+, 95%"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Icon</Label>
                    <Select
                      value={editItem.icon || 'trending-up'}
                      onValueChange={(v) => setEditItem({...editItem, icon: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="users">Users</SelectItem>
                        <SelectItem value="trending-up">Trending Up</SelectItem>
                        <SelectItem value="award">Award</SelectItem>
                        <SelectItem value="calendar">Calendar</SelectItem>
                        <SelectItem value="target">Target</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={editItem.description || ''}
                      onChange={(e) => setEditItem({...editItem, description: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editItem.is_active !== false}
                      onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                    />
                    <Label>Active</Label>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Modal */}
      <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Page from Template</DialogTitle>
            <DialogDescription>Select an existing page to use as a template</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {allPages.map(p => (
              <div 
                key={p.id}
                className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer flex items-center justify-between"
                onClick={() => createFromTemplate(p)}
              >
                <div>
                  <p className="font-medium">{p.title}</p>
                  <p className="text-sm text-muted-foreground">{p.path}</p>
                </div>
                <Button variant="outline" size="sm">
                  <Copy className="w-4 h-4 mr-2" /> Use
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Draft Modal */}
      <Dialog open={publishModalOpen} onOpenChange={setPublishModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Draft</DialogTitle>
            <DialogDescription>
              This will replace the live content with your draft changes. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              A backup version will be created automatically before publishing.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishModalOpen(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={publishDraft} disabled={saving}>
              {saving ? 'Publishing...' : 'Publish Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>View and restore previous versions of this page</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-2 py-4">
              {versions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No version history available yet.</p>
              ) : (
                versions.map((version) => (
                  <div key={version.id} className="p-4 border rounded-lg flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">v{version.version_number}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(version.created_at).toLocaleString()}
                        </span>
                      </div>
                      {version.change_summary && (
                        <p className="text-sm mt-1">{version.change_summary}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        By: {version.created_by || 'Unknown'}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => rollbackToVersion(version.id)}
                      disabled={saving}
                    >
                      <History className="w-4 h-4 mr-2" />
                      Restore
                    </Button>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Schedule Publish Modal */}
      <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Publishing</DialogTitle>
            <DialogDescription>Schedule this page to be published at a future time</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Publish Date & Time</Label>
              <Input
                type="datetime-local"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
            
            {scheduledPublishes.length > 0 && (
              <div className="space-y-2">
                <Label>Currently Scheduled</Label>
                <div className="space-y-2">
                  {scheduledPublishes.map((scheduled) => (
                    <div key={scheduled.id} className="p-3 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          {new Date(scheduled.scheduled_time).toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Status: {scheduled.status}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => cancelScheduledPublish(scheduled.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleModalOpen(false)}>Cancel</Button>
            <Button onClick={schedulePublish} disabled={saving || !scheduleDate}>
              <Calendar className="w-4 h-4 mr-2" />
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Component Library Modal */}
      <Dialog open={componentLibraryOpen} onOpenChange={setComponentLibraryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Add Section from Component Library</DialogTitle>
            <DialogDescription>Choose a pre-built section to add to your page</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {componentTemplates.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No component templates available</p>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    await adminApi.seedComponentTemplates();
                    const res = await adminApi.getComponentTemplates();
                    setComponentTemplates(res.data || []);
                    toast.success('Templates loaded');
                  }}
                >
                  <Package className="w-4 h-4 mr-2" /> Load Default Templates
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
                {componentTemplates.filter(t => t.is_active).map((template) => (
                  <Card 
                    key={template.id} 
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => addSectionFromTemplate(template)}
                  >
                    {template.thumbnail_url ? (
                      <div className="aspect-video bg-muted">
                        <img 
                          src={template.thumbnail_url} 
                          alt={template.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-muted flex items-center justify-center">
                        <Layout className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    <CardContent className="pt-3">
                      <h4 className="font-medium text-sm">{template.name}</h4>
                      <Badge variant="secondary" className="mt-1 text-xs">{template.category}</Badge>
                      {template.description && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{template.description}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComponentLibraryOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => navigate('/admin/components')}>
              Manage Components
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Code Modal */}
      <Dialog open={customCodeOpen} onOpenChange={setCustomCodeOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Custom Code Injection</DialogTitle>
            <DialogDescription>Add custom CSS and JavaScript to this page</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="css">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="css">CSS</TabsTrigger>
              <TabsTrigger value="js">JavaScript</TabsTrigger>
              <TabsTrigger value="head">Head Scripts</TabsTrigger>
              <TabsTrigger value="body">Body Scripts</TabsTrigger>
            </TabsList>
            
            <TabsContent value="css" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Custom CSS</Label>
                <Textarea
                  value={customCode.custom_css}
                  onChange={(e) => setCustomCode({ ...customCode, custom_css: e.target.value })}
                  placeholder=".my-class { color: red; }"
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  CSS will be injected into the page &lt;head&gt;
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="js" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Custom JavaScript</Label>
                <Textarea
                  value={customCode.custom_js}
                  onChange={(e) => setCustomCode({ ...customCode, custom_js: e.target.value })}
                  placeholder="console.log('Custom script loaded');"
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  JavaScript will be executed after page load
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="head" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Head Scripts (e.g., analytics, fonts)</Label>
                <Textarea
                  value={customCode.head_scripts}
                  onChange={(e) => setCustomCode({ ...customCode, head_scripts: e.target.value })}
                  placeholder='<script async src="https://..."></script>'
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Scripts injected into &lt;head&gt; before closing tag
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="body" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Body Scripts (e.g., chat widgets)</Label>
                <Textarea
                  value={customCode.body_scripts}
                  onChange={(e) => setCustomCode({ ...customCode, body_scripts: e.target.value })}
                  placeholder='<script src="https://..."></script>'
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Scripts injected before &lt;/body&gt; closing tag
                </p>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomCodeOpen(false)}>Cancel</Button>
            <Button onClick={saveCustomCode} disabled={saving}>
              {saving ? 'Saving...' : 'Save Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Sections Panel (for advanced D&D) */}
      {pageSections.length > 0 && (
        <Dialog>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Page Sections</DialogTitle>
              <DialogDescription>Drag to reorder sections on the page</DialogDescription>
            </DialogHeader>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext items={pageSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2 py-4">
                  {pageSections.map((section) => (
                    <SortableItem key={section.id} id={section.id}>
                      <div className="p-3 border rounded-lg flex items-center justify-between ml-4">
                        <div className="flex items-center gap-3">
                          <Layout className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{section.section_name}</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive"
                          onClick={() => deletePageSection(section.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default FullPageEditor;
